/**
 * Hala Control Center - Executive Dashboard
 * Hala Session Conversion Funnel Dashboard
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  MessageSquare, 
  TrendingUp, 
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  Target,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  ArrowRight,
  Percent,
  Inbox
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/control-center`;

// Auth helper
const getAuthHeaders = () => {
  const token = localStorage.getItem('hala_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const COLORS = {
  primary: '#1B3A6B',
  primaryLight: '#2A4A7A',
  gold: '#C5A572',
  success: '#1D8F4E',
  warning: '#F59E0B',
  error: '#EF4444',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  textLight: '#94A3B8',
};

// Reusable KPI Card
function KPICard({ title, value, subtitle, icon: Icon, color, trend, trendValue }) {
  return (
    <div style={{
      backgroundColor: COLORS.white,
      borderRadius: '16px',
      padding: '24px',
      border: `1px solid ${COLORS.border}`,
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          backgroundColor: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Icon size={24} color={color} />
        </div>
        {trend && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 8px',
            borderRadius: '6px',
            backgroundColor: trend === 'up' ? `${COLORS.success}15` : `${COLORS.error}15`,
            color: trend === 'up' ? COLORS.success : COLORS.error,
            fontSize: '12px',
            fontWeight: 600
          }}>
            {trend === 'up' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            {trendValue}%
          </div>
        )}
      </div>
      <h3 style={{ fontSize: '32px', fontWeight: 'bold', color: COLORS.text, margin: '0 0 4px' }}>{value}</h3>
      <p style={{ fontSize: '14px', color: COLORS.textMuted, margin: 0 }}>{title}</p>
      {subtitle && (
        <p style={{ fontSize: '12px', color: COLORS.textLight, margin: '8px 0 0' }}>{subtitle}</p>
      )}
    </div>
  );
}

// Simple Bar Chart
function SimpleBarChart({ data, title }) {
  const maxValue = Math.max(...data.map(d => d.count), 1);
  
  return (
    <div style={{
      backgroundColor: COLORS.white,
      borderRadius: '16px',
      padding: '24px',
      border: `1px solid ${COLORS.border}`,
      height: '100%'
    }}>
      <h3 style={{ fontSize: '16px', fontWeight: 600, color: COLORS.text, margin: '0 0 20px' }}>{title}</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {data.slice(0, 5).map((item, i) => (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '13px', color: COLORS.text }}>{item.name}</span>
              <span style={{ fontSize: '13px', fontWeight: 600, color: COLORS.primary }}>{item.count}</span>
            </div>
            <div style={{
              height: '8px',
              backgroundColor: COLORS.bgDark,
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                width: `${(item.count / maxValue) * 100}%`,
                backgroundColor: i === 0 ? COLORS.primary : i === 1 ? COLORS.gold : COLORS.success,
                borderRadius: '4px',
                transition: 'width 0.5s ease'
              }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Recent Sessions Table - Updated columns for conversion tracking
function RecentSessionsTable({ sessions }) {
  const statusColors = {
    'مكتملة': COLORS.success,
    'متروكة': COLORS.error,
    'قيد الاكتشاف': COLORS.warning,
    'توصية جاهزة': COLORS.primary,
    'تم التحليل': COLORS.primary,
    'خدمة نشطة': COLORS.success,
    'تم الإرسال': COLORS.success,
    'جديدة': COLORS.textMuted,
    'قيد التحليل': COLORS.warning,
  };

  return (
    <div style={{
      backgroundColor: COLORS.white,
      borderRadius: '16px',
      border: `1px solid ${COLORS.border}`,
      overflow: 'hidden'
    }}>
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: COLORS.text, margin: 0 }}>آخر الجلسات</h3>
          <Link to="/admin/sessions" style={{ fontSize: '13px', color: COLORS.primary, textDecoration: 'none' }}>
            عرض الكل
          </Link>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: COLORS.bg }}>
              <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>رقم الجلسة</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>الاحتياج</th>
              <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>توصية</th>
              <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>طلب</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>الحالة</th>
            </tr>
          </thead>
          <tbody>
            {sessions.slice(0, 7).map((session, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                <td style={{ padding: '14px 16px' }}>
                  <Link 
                    to={`/admin/sessions/${session.session_id}`}
                    style={{ 
                      fontSize: '13px', 
                      color: COLORS.primary, 
                      fontWeight: 500,
                      textDecoration: 'none'
                    }}
                  >
                    {session.session_id?.substring(0, 12)}...
                  </Link>
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <span style={{ fontSize: '13px', color: COLORS.textMuted }}>
                    {session.last_intent || session.main_problem || session.detected_need || '-'}
                  </span>
                </td>
                <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                  {session.recommendation_generated ? (
                    <CheckCircle size={18} color={COLORS.success} />
                  ) : (
                    <XCircle size={18} color={COLORS.textLight} />
                  )}
                </td>
                <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                  {session.request_created ? (
                    <CheckCircle size={18} color={COLORS.success} />
                  ) : (
                    <XCircle size={18} color={COLORS.textLight} />
                  )}
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    backgroundColor: `${statusColors[session.status_ar] || COLORS.primary}15`,
                    color: statusColors[session.status_ar] || COLORS.primary,
                    fontSize: '12px',
                    fontWeight: 500
                  }}>
                    {session.status_ar || session.session_status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Alert Card
function AlertCard({ alert }) {
  const typeColors = {
    error: COLORS.error,
    warning: COLORS.warning,
    info: COLORS.primary
  };

  return (
    <Link 
      to={alert.action}
      style={{
        display: 'block',
        padding: '16px',
        backgroundColor: `${typeColors[alert.type]}10`,
        borderRadius: '12px',
        border: `1px solid ${typeColors[alert.type]}30`,
        textDecoration: 'none',
        marginBottom: '12px'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '8px',
          backgroundColor: `${typeColors[alert.type]}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <AlertTriangle size={18} color={typeColors[alert.type]} />
        </div>
        <div>
          <h4 style={{ fontSize: '14px', fontWeight: 600, color: COLORS.text, margin: '0 0 4px' }}>{alert.title}</h4>
          <p style={{ fontSize: '13px', color: COLORS.textMuted, margin: 0 }}>{alert.message}</p>
        </div>
      </div>
    </Link>
  );
}

// Conversion Funnel Widget
function FunnelWidget({ funnel }) {
  const steps = [
    { label: 'الجلسات', value: funnel?.sessions || 0, color: COLORS.primary },
    { label: 'التوصيات', value: funnel?.recommendations || 0, color: COLORS.gold },
    { label: 'الطلبات', value: funnel?.requests || 0, color: COLORS.success },
  ];

  return (
    <div style={{
      backgroundColor: COLORS.white,
      borderRadius: '16px',
      padding: '20px',
      border: `1px solid ${COLORS.border}`
    }}>
      <h3 style={{ fontSize: '16px', fontWeight: 600, color: COLORS.text, margin: '0 0 16px' }}>
        قمع التحويل اليوم
      </h3>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {steps.map((step, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{ 
                fontSize: '28px', 
                fontWeight: 'bold', 
                color: step.color, 
                margin: '0 0 4px' 
              }}>
                {step.value}
              </p>
              <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: 0 }}>
                {step.label}
              </p>
            </div>
            {i < steps.length - 1 && (
              <ArrowRight 
                size={20} 
                color={COLORS.border} 
                style={{ margin: '0 16px' }} 
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const [kpis, setKpis] = useState(null);
  const [trends, setTrends] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const headers = getAuthHeaders();
      const [kpisRes, trendsRes, alertsRes, sessionsRes] = await Promise.all([
        fetch(`${API}/dashboard/kpis`, { headers }),
        fetch(`${API}/dashboard/trends`, { headers }),
        fetch(`${API}/dashboard/alerts`, { headers }),
        fetch(`${API}/sessions?limit=10`, { headers })
      ]);

      const kpisData = await kpisRes.json();
      const trendsData = await trendsRes.json();
      const alertsData = await alertsRes.json();
      const sessionsData = await sessionsRes.json();

      setKpis(kpisData);
      setTrends(trendsData);
      setAlerts(alertsData || []);
      setSessions(sessionsData?.items || []);
    } catch (e) {
      console.error('Failed to fetch dashboard data:', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: `4px solid ${COLORS.border}`,
            borderTopColor: COLORS.primary,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: COLORS.textMuted }}>جاري تحميل البيانات...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: COLORS.text, margin: '0 0 8px' }}>لوحة المعلومات</h1>
        <p style={{ fontSize: '14px', color: COLORS.textMuted, margin: 0 }}>تحويل جلسات هلا إلى طلبات</p>
      </div>

      {/* KPI Cards - 4 Main Metrics */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: '20px',
        marginBottom: '32px'
      }}>
        <KPICard
          title="جلسات اليوم"
          value={kpis?.sessions_today || 0}
          subtitle="عدد الجلسات الجديدة اليوم"
          icon={MessageSquare}
          color={COLORS.primary}
        />
        <KPICard
          title="طلبات من هلا"
          value={kpis?.requests_from_hala_today || 0}
          subtitle="طلبات تم إنشاؤها اليوم"
          icon={FileText}
          color={COLORS.success}
        />
        <KPICard
          title="جلسات بدون تحويل"
          value={kpis?.sessions_without_conversion || 0}
          subtitle="جلسات لم تُحوّل لطلبات"
          icon={Inbox}
          color={COLORS.warning}
        />
        <KPICard
          title="نسبة التحويل"
          value={`${kpis?.conversion_rate_today || 0}%`}
          subtitle="الطلبات ÷ الجلسات"
          icon={Percent}
          color={kpis?.conversion_rate_today >= 50 ? COLORS.success : COLORS.warning}
        />
      </div>

      {/* Main Content Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '24px', marginBottom: '32px' }}>
        {/* Left Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Recent Sessions */}
          <RecentSessionsTable sessions={sessions} />
        </div>

        {/* Right Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Conversion Funnel */}
          <FunnelWidget funnel={kpis?.funnel} />
          
          {/* Alerts */}
          <div style={{
            backgroundColor: COLORS.white,
            borderRadius: '16px',
            padding: '20px',
            border: `1px solid ${COLORS.border}`
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: COLORS.text, margin: '0 0 16px' }}>تنبيهات النظام</h3>
            {alerts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px', color: COLORS.textMuted }}>
                <CheckCircle size={32} color={COLORS.success} style={{ marginBottom: '12px' }} />
                <p style={{ margin: 0 }}>لا توجد تنبيهات</p>
              </div>
            ) : (
              alerts.map((alert, i) => <AlertCard key={i} alert={alert} />)
            )}
          </div>
        </div>
      </div>

      {/* Bottom Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
        {trends?.top_needs && trends.top_needs.length > 0 && (
          <SimpleBarChart data={trends.top_needs} title="أكثر الاحتياجات شيوعاً" />
        )}
        {trends?.top_services && trends.top_services.length > 0 && (
          <SimpleBarChart data={trends.top_services} title="أكثر الخدمات طلباً" />
        )}
      </div>
    </div>
  );
}
